import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/hooks/empresa/organizacion/use-businesses'

// Escritos a mano, no alias de `api-schema`: espejo de los modelos de respuesta del router.
export type ConfirmedBy = {id: string; name: string; last_name: string; is_system: boolean}

type FactSource = 'owner_declared' | 'owner_confirmed' | 'ciiu_suggested' | 'derived' | 'ai_imported'

export type ProfileFact = {
  fact_key: string
  prompt_es: string
  value_type: string
  enum_values: string[] | null
  value: unknown
  source: FactSource | null
  confirmed_by: ConfirmedBy | null
  confirmed_at: string | null
}

type HazardSource = 'ciiu_suggested' | 'owner_added' | 'owner_confirmed' | 'owner_denied'

export type HazardTag = {
  tag_code: string
  label: string
  description: string
  source: HazardSource
  is_reask: boolean
  confirmed_by: ConfirmedBy | null
  confirmed_at: string | null
  denied_at: string | null
  denial_reason: string | null
}

export type CatalogHazard = {tag_code: string; label: string; description: string}

export type HazardsState = {
  pending: HazardTag[]
  confirmed: HazardTag[]
  denied: HazardTag[]
  addable: CatalogHazard[]
}

export type Municipio = {code: string; nombre: string; departamento: string}

export function useMunicipioSearch(businessId: string | undefined, query: string) {
  return useQuery({
    queryKey: ['perfil-municipios', businessId, query],
    queryFn: async () =>
      (
        await api.get<Page<Municipio>>(`/businesses/${businessId}/perfil-riesgo/municipios`, {
          params: {search: query, page_size: 8},
        })
      ).data,
    enabled: !!businessId && query.trim().length > 0,
    placeholderData: (previous) => previous,
  })
}

/** Resolves a confirmed municipio fact's bare DANE code to its name/departamento. */
export function useMunicipio(businessId: string | undefined, code: string | null | undefined) {
  return useQuery({
    queryKey: ['perfil-municipio', businessId, code],
    queryFn: async () => (await api.get<Municipio>(`/businesses/${businessId}/perfil-riesgo/municipios/${code}`)).data,
    enabled: !!businessId && !!code,
  })
}

export function useProfileFacts(businessId: string | undefined) {
  return useQuery({
    queryKey: ['perfil-facts', businessId],
    queryFn: async () => (await api.get<ProfileFact[]>(`/businesses/${businessId}/perfil-riesgo/facts`)).data,
    enabled: !!businessId,
  })
}

export function useUpdateProfileFact(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {fact_key: string; value: unknown}) =>
      (await api.put<ProfileFact>(`/businesses/${businessId}/perfil-riesgo/facts`, input)).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['perfil-facts', businessId]}),
  })
}

export function useHazards(businessId: string | undefined) {
  return useQuery({
    queryKey: ['perfil-hazards', businessId],
    queryFn: async () => (await api.get<HazardsState>(`/businesses/${businessId}/perfil-riesgo/hazards`)).data,
    enabled: !!businessId,
  })
}

function useHazardsInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  // Confirming/denying quimicos gates the sustancias_especificas fact, so a
  // hazard mutation also invalidates facts (the multi-select's lock state).
  return () => {
    queryClient.invalidateQueries({queryKey: ['perfil-hazards', businessId]})
    queryClient.invalidateQueries({queryKey: ['perfil-facts', businessId]})
  }
}

export function useAddHazard(businessId: string) {
  const invalidate = useHazardsInvalidation(businessId)
  return useMutation({
    mutationFn: async (tagCode: string) =>
      (await api.post<HazardTag>(`/businesses/${businessId}/perfil-riesgo/hazards`, {tag_code: tagCode})).data,
    onSuccess: invalidate,
  })
}

export function useConfirmHazard(businessId: string) {
  const invalidate = useHazardsInvalidation(businessId)
  return useMutation({
    mutationFn: async (tagCode: string) =>
      (await api.post<HazardTag>(`/businesses/${businessId}/perfil-riesgo/hazards/${tagCode}/confirm`, {})).data,
    onSuccess: invalidate,
  })
}

export function useDenyHazard(businessId: string) {
  const invalidate = useHazardsInvalidation(businessId)
  return useMutation({
    mutationFn: async ({tagCode, reason}: {tagCode: string; reason?: string | null}) =>
      (
        await api.post<HazardTag>(`/businesses/${businessId}/perfil-riesgo/hazards/${tagCode}/deny`, {
          reason: reason ?? null,
        })
      ).data,
    onSuccess: invalidate,
  })
}
